export type HtmlImageStatus = "missing" | "loading" | "error" | "ready";
export type HtmlVideoStatus = "missing" | "loading" | "error" | "ready";

export function getHtmlImageStatus(img: HTMLImageElement | null | undefined): HtmlImageStatus {
  if (!img) return "missing";
  const src = (img.currentSrc || img.src || "").trim();
  if (!src) return "missing";
  if (!img.complete) return "loading";
  if (img.naturalWidth > 0 && img.naturalHeight > 0) return "ready";
  return "error";
}

export function isRenderableHtmlImage(img: HTMLImageElement | null | undefined): img is HTMLImageElement {
  return getHtmlImageStatus(img) === "ready";
}

export async function waitForHtmlImage(
  img: HTMLImageElement,
  timeoutMs = 15_000
): Promise<"loaded" | "error" | "timeout"> {
  const status = getHtmlImageStatus(img);
  if (status === "ready") return "loaded";
  if (status === "error") return "error";

  return await new Promise((resolve) => {
    let done = false;
    const cleanup = (result: "loaded" | "error" | "timeout") => {
      if (done) return;
      done = true;
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
      clearTimeout(timer);
      resolve(result);
    };

    const onLoad = async () => {
      try {
        // Best-effort: ensure image is decoded before we allow drawing.
        // decode() can reject for some images; the naturalWidth check is the main guard.
        await img.decode?.();
      } catch {}
      cleanup("loaded");
    };
    const onError = () => cleanup("error");

    img.addEventListener("load", onLoad, { once: true });
    img.addEventListener("error", onError, { once: true });

    const timer = window.setTimeout(() => cleanup("timeout"), Math.max(0, timeoutMs));
  });
}

export function getHtmlVideoStatus(video: HTMLVideoElement | null | undefined): HtmlVideoStatus {
  if (!video) return "missing";
  if (video.error) return "error";
  // HAVE_CURRENT_DATA (2) is the minimum for drawImage(video) to be reliably safe.
  if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) return "ready";
  return "loading";
}

export function isRenderableHtmlVideo(video: HTMLVideoElement | null | undefined): video is HTMLVideoElement {
  return getHtmlVideoStatus(video) === "ready";
}

